import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUpRight, Briefcase, HeartHandshake, Sparkles, CheckCircle2 } from "lucide-react";

const GH_LOGO = "https://customer-assets.emergentagent.com/job_peer-support-elite/artifacts/uxvvui3f_gh%20logo.png";
const GH_URL = "https://generationalh.com";

// Brand tokens (Generational Health palette)
const GH_NAVY = "#0B1F3A";
const GH_TEAL = "#16A5BD";

export default function GenerationalHealth() {
  const { user } = useAuth();
  return (
    <div data-testid="gh-page">
      {/* HERO — Generational Health brand */}
      <section style={{ background: GH_NAVY }} className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" aria-hidden style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, #16A5BD 0%, transparent 50%), radial-gradient(circle at 80% 60%, #16A5BD 0%, transparent 50%)",
        }} />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[0.72rem] tracking-[0.22em] uppercase mb-6" style={{ color: GH_TEAL }} data-testid="gh-eyebrow">
              A Carolina Peer Institute Partner
            </div>
            <h1 className="font-serif text-[clamp(2.6rem,6vw,5rem)] leading-[0.95] tracking-tight text-white">
              Where trained peers
              <br />
              <span style={{ color: GH_TEAL }} className="italic">go to work.</span>
            </h1>
            <p className="mt-8 text-lg text-white/80 max-w-[58ch] leading-relaxed">
              Generational Health is the employment home for Carolina Peer Institute graduates —
              a behavioral health organization that hires Certified Peer Support Specialists,
              pays them like the professionals they are, and supports the long arc of their career.
            </p>
            <p className="mt-4 text-lg italic font-serif" style={{ color: GH_TEAL }}>
              Your mind. Your power.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#interest"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-[0.08em] uppercase font-medium transition-all"
                style={{ background: GH_TEAL, color: GH_NAVY }}
                data-testid="gh-cta-interest"
              >
                Express employment interest <ArrowUpRight size={16} strokeWidth={1.8} />
              </a>
              <a
                href={GH_URL} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-[0.08em] uppercase font-medium border transition-all hover:bg-white/10"
                style={{ borderColor: "rgba(255,255,255,0.4)", color: "#fff" }}
                data-testid="gh-cta-website"
              >
                Visit generationalh.com <ArrowUpRight size={16} strokeWidth={1.8} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 flex items-center justify-center">
            <img
              src={GH_LOGO}
              alt="Generational Health"
              className="w-full max-w-[420px] aspect-square object-contain"
              data-testid="gh-logo"
            />
          </div>
        </div>
      </section>

      {/* WHY GH */}
      <section className="bg-bone border-b border-stone">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow mb-3" style={{ color: GH_TEAL }}>Why Generational Health</div>
            <h2 className="font-serif text-4xl text-ink leading-tight">
              A behavioral health home built for peers — by people who understand the work.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 container-prose text-[1.05rem] leading-[1.85] text-foreground/90">
            <p>
              Generational Health is a North Carolina behavioral health organization with a focus
              on community-rooted, recovery-oriented care. We believe the peer workforce is the
              single most important investment in the future of mental health, and we structure
              our team and our practice to reflect that.
            </p>
            <p className="mt-6">
              That means competitive pay, real supervision, continuing education stipends, and
              a culture where lived experience is treated as the clinical asset it is — not a
              footnote. Carolina Peer Institute graduates have a direct path into our hiring
              pipeline.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-24">
        <div className="editorial-rule mb-12">For Carolina Peer Institute graduates</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-stone">
          {[
            {
              icon: Briefcase,
              title: "Active hiring pipeline",
              text: "Open Certified Peer Support Specialist roles across the Charlotte metro and broader North Carolina. We prioritize candidates who hold — or are working toward — NCCPSS certification."
            },
            {
              icon: HeartHandshake,
              title: "Supervision that respects you",
              text: "Documented clinical and administrative supervision built into your week — not bolted on. Because supervision is your single highest-leverage protective factor."
            },
            {
              icon: Sparkles,
              title: "Career, not just a job",
              text: "Continuing education stipends, advancement tracks, and the kind of role that grows. We hire peers who plan to be in this work in twenty years."
            },
          ].map((card, i) => (
            <div key={i} className="border-r border-b border-stone p-8 bg-white" data-testid={`gh-benefit-${i}`}>
              <card.icon size={28} strokeWidth={1.4} style={{ color: GH_TEAL }} className="mb-6" />
              <h3 className="font-serif text-2xl text-ink mb-3">{card.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTEREST FORM */}
      <section id="interest" style={{ background: GH_NAVY }} className="text-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[0.72rem] tracking-[0.22em] uppercase mb-4" style={{ color: GH_TEAL }}>
              Step Forward
            </div>
            <h2 className="font-serif text-4xl leading-tight mb-6">
              Tell us you're interested — we'll reach out.
            </h2>
            <p className="text-white/80 leading-relaxed">
              Whether you've just begun the 40-hour training or you've already earned your
              certificate, share a few details and the Generational Health team will be in touch
              about openings that fit you.
            </p>
            <p className="mt-4 text-sm text-white/60">
              Your information is shared only with the Generational Health hiring team.
            </p>
          </div>
          <div className="lg:col-span-7">
            <InterestForm currentUser={user} />
          </div>
        </div>
      </section>

      {/* BACK TO TRAINING */}
      <section className="bg-bone py-16 text-center">
        <Link
          to={user ? "/dashboard" : "/"}
          className="btn-ghost"
          data-testid="gh-back-link"
        >
          ← Back to {user ? "your training" : "Carolina Peer Institute"}
        </Link>
      </section>
    </div>
  );
}

function InterestForm({ currentUser }) {
  const [name, setName] = useState(currentUser?.name || "");
  const [email, setEmail] = useState(currentUser?.email || "");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/employment-interest", {
        name, email,
        phone: phone || undefined,
        city: city || undefined,
        role_interest: role || undefined,
        message: message || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white/5 border border-white/20 p-10 text-center" data-testid="gh-interest-success">
        <CheckCircle2 size={48} strokeWidth={1.4} style={{ color: "#16A5BD" }} className="mx-auto mb-4" />
        <h3 className="font-serif text-3xl mb-3">Thank you — we have your information.</h3>
        <p className="text-white/80 max-w-[40ch] mx-auto">
          The Generational Health hiring team will reach out to <span className="text-white">{email}</span> with next steps.
        </p>
      </div>
    );
  }

  const inputCls = "w-full bg-white/5 border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#16A5BD] transition-colors";
  const labelCls = "text-[0.72rem] tracking-[0.22em] uppercase text-white/70 block mb-2";

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="gh-interest-form">
      {error && (
        <div className="bg-red-500/10 border border-red-400/40 text-red-200 px-4 py-3 text-sm" data-testid="gh-interest-error">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className={labelCls}>Full name *</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="gh-name" />
        </label>
        <label className="block">
          <span className={labelCls}>Email *</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="gh-email" />
        </label>
        <label className="block">
          <span className={labelCls}>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="(704) 555-0100" data-testid="gh-phone" />
        </label>
        <label className="block">
          <span className={labelCls}>City / Region</span>
          <input value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} placeholder="Charlotte, NC" data-testid="gh-city" />
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>Role of interest</span>
        <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="gh-role">
          <option value="" className="bg-[#0B1F3A]">— Select one —</option>
          <option value="Certified Peer Support Specialist" className="bg-[#0B1F3A]">Certified Peer Support Specialist</option>
          <option value="Peer Support — In Training" className="bg-[#0B1F3A]">Peer Support — In Training</option>
          <option value="Group Facilitator" className="bg-[#0B1F3A]">Group Facilitator</option>
          <option value="Recovery Coach" className="bg-[#0B1F3A]">Recovery Coach</option>
          <option value="Other / Open" className="bg-[#0B1F3A]">Other / Open</option>
        </select>
      </label>
      <label className="block">
        <span className={labelCls}>A bit about you (optional)</span>
        <textarea
          rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
          className={inputCls} placeholder="Lived experience, what you're looking for, availability…"
          data-testid="gh-message"
        />
      </label>
      <button
        type="submit" disabled={busy}
        className="inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-[0.08em] uppercase font-medium transition-all disabled:opacity-60"
        style={{ background: GH_TEAL, color: GH_NAVY }}
        data-testid="gh-interest-submit"
      >
        {busy ? "Submitting…" : "Submit interest"} <ArrowUpRight size={16} strokeWidth={1.8} />
      </button>
    </form>
  );
}
